import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Clock, Flame, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, "0");

const TimeBox = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[56px] sm:min-w-[70px]">
    <div className="bg-black/40 border border-white/20 rounded-lg px-2 py-2 sm:px-3 sm:py-3 backdrop-blur-sm">
      <span className="text-white font-black text-xl sm:text-3xl font-mono tabular-nums">
        {pad(value)}
      </span>
    </div>
    <span className="text-white/70 text-[10px] sm:text-xs uppercase mt-1 tracking-wider">
      {label}
    </span>
  </div>
);

/**
 * Tarjeta promocional inline (grande y prominente), pensada para mostrar
 * dentro de Home (bajo hero) y Courses (arriba del listado).
 * Se auto-oculta si no hay promo activa.
 */
const PromotionCard = () => {
  const [promo, setPromo] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const fetchPromo = async () => {
    try {
      const { data } = await axios.get(`${API}/promotion`);
      if (data.is_live) {
        setPromo(data);
        setSecondsLeft(data.seconds_left);
      } else {
        setPromo(null);
      }
    } catch {
      setPromo(null);
    }
  };

  useEffect(() => {
    fetchPromo();
    const refresh = setInterval(fetchPromo, 60000);
    return () => clearInterval(refresh);
  }, []);

  useEffect(() => {
    if (!promo || secondsLeft <= 0) return;
    const tick = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setPromo(null);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [promo, secondsLeft]);

  if (!promo || secondsLeft <= 0) return null;

  const days = Math.floor(secondsLeft / 86400);
  const hours = Math.floor((secondsLeft % 86400) / 3600);
  const mins = Math.floor((secondsLeft % 3600) / 60);
  const secs = secondsLeft % 60;

  const monthlyOriginal = 10;
  const annualOriginal = 100;
  const monthlyFinal = (monthlyOriginal * (100 - promo.discount_percentage) / 100).toFixed(2);
  const annualFinal = (annualOriginal * (100 - promo.discount_percentage) / 100).toFixed(2);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#E50914]/40 shadow-[0_0_40px_rgba(229,9,20,0.25)]" data-testid="promotion-card-inline">
      {/* Background gradient + noise */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#E50914] via-red-700 to-black" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.15),transparent_60%)]" />

      <div className="relative z-10 px-5 py-6 sm:px-8 sm:py-8 flex flex-col lg:flex-row items-center gap-6 lg:gap-8">
        {/* Left: badge + title + description */}
        <div className="flex-1 min-w-0 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3 py-1 mb-3">
            <Flame className="w-4 h-4 text-yellow-300" />
            <span className="text-white text-xs sm:text-sm font-bold uppercase tracking-widest">
              Oferta activa
            </span>
          </div>
          <h3 className="text-white text-2xl sm:text-3xl lg:text-4xl font-black leading-tight mb-2">
            {promo.title || "¡Oferta por tiempo limitado!"}
          </h3>
          {promo.description && (
            <p className="text-white/85 text-sm sm:text-base mb-3 max-w-xl">
              {promo.description}
            </p>
          )}

          {/* Prices row */}
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 mb-4">
            {promo.applies_to_monthly && (
              <div className="flex items-baseline gap-2">
                <span className="text-white/60 line-through text-sm">${monthlyOriginal}</span>
                <span className="text-white font-black text-2xl">${monthlyFinal}</span>
                <span className="text-white/80 text-xs">/mes</span>
              </div>
            )}
            {promo.applies_to_annual && (
              <div className="flex items-baseline gap-2">
                <span className="text-white/60 line-through text-sm">${annualOriginal}</span>
                <span className="text-white font-black text-2xl">${annualFinal}</span>
                <span className="text-white/80 text-xs">/año</span>
              </div>
            )}
            {promo.discount_percentage > 0 && (
              <span className="bg-yellow-300 text-black px-3 py-1 rounded-full font-black text-sm">
                {promo.discount_percentage}% OFF
              </span>
            )}
          </div>
        </div>

        {/* Right: countdown + CTA */}
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-1.5 text-white/90 text-xs uppercase tracking-widest">
            <Clock className="w-4 h-4" />
            <span>Termina en</span>
          </div>
          <div className="flex gap-2 sm:gap-3" data-testid="promotion-card-countdown">
            {days > 0 && <TimeBox value={days} label="Días" />}
            <TimeBox value={hours} label="Horas" />
            <TimeBox value={mins} label="Min" />
            <TimeBox value={secs} label="Seg" />
          </div>
          <Link to="/contaflix-pro" className="w-full">
            <button
              className="w-full mt-1 bg-white hover:bg-white/90 text-[#E50914] font-black px-6 py-3 rounded-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 uppercase tracking-wider text-sm"
              data-testid="promotion-card-cta"
            >
              Aprovechar ahora
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PromotionCard;
